import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLazyGetEmissorOrderQuery, useLazyListEmissorOrderBeneficiariesQuery, useLazyListEmissorOrdersQuery, } from '../services/contractOrdersApi';
import { mapEmissorOrderToFormData } from '../utils/mapEmissorOrderToFormData';
const BENEFICIARIES_PAGE_SIZE = 100;
export function useEmissorRepeatOrderInitializer({ enabled, contractId, products, productsLoading, competenceDate, creditDate, businessDays, businessDaysLoading, setFormData, onSuccess, }) {
    const [searchParams] = useSearchParams();
    const repeatLast = searchParams.get('repeat') === 'last';
    const selectedOrderId = searchParams.get('orderId');
    const shouldRepeat = enabled && (repeatLast || Boolean(selectedOrderId));
    const [isRunning, setIsRunning] = useState(false);
    const [initializationError, setInitializationError] = useState(null);
    const hasRunRef = useRef(false);
    const [listOrders] = useLazyListEmissorOrdersQuery();
    const [getOrder] = useLazyGetEmissorOrderQuery();
    const [listOrderBeneficiaries] = useLazyListEmissorOrderBeneficiariesQuery();
    const getAllOrderBeneficiaries = useCallback(async (orderId) => {
        if (!contractId) {
            return [];
        }
        const beneficiaries = [];
        let offset = 0;
        let total = Number.POSITIVE_INFINITY;
        while (beneficiaries.length < total) {
            const page = await listOrderBeneficiaries({
                contractId,
                orderId,
                limit: BENEFICIARIES_PAGE_SIZE,
                offset,
            }).unwrap();
            const result = page.result ?? [];
            total = page.total ?? result.length;
            beneficiaries.push(...result);
            if (result.length === 0) {
                break;
            }
            offset += result.length;
        }
        return beneficiaries;
    }, [contractId, listOrderBeneficiaries]);
    const initialize = useCallback(async () => {
        if (!contractId || !shouldRepeat) {
            return;
        }
        setIsRunning(true);
        setInitializationError(null);
        try {
            let orderId = selectedOrderId;
            if (repeatLast) {
                const orders = await listOrders({ contractId, limit: 1, sort: '-createdAt' }).unwrap();
                orderId = orders.result?.[0]?.id ?? null;
            }
            if (!orderId) {
                throw new Error('Order not found');
            }
            const [order, beneficiaries] = await Promise.all([
                getOrder({ contractId, orderId }).unwrap(),
                getAllOrderBeneficiaries(orderId),
            ]);
            const product = products.find(item => item.productId === order.product?.id);
            if (!product) {
                throw new Error('Order product not found');
            }
            if (order.calculationType === 'DAILY' && businessDays <= 0) {
                throw new Error('Business days not found');
            }
            setFormData(mapEmissorOrderToFormData({
                order,
                beneficiaries,
                product,
                competenceDate,
                creditDate,
                businessDays,
            }));
            onSuccess();
        }
        catch (error) {
            setInitializationError(error);
        }
        finally {
            setIsRunning(false);
        }
    }, [
        businessDays,
        competenceDate,
        contractId,
        creditDate,
        getAllOrderBeneficiaries,
        getOrder,
        listOrders,
        onSuccess,
        products,
        repeatLast,
        selectedOrderId,
        setFormData,
        shouldRepeat,
    ]);
    useEffect(() => {
        if (!shouldRepeat || !contractId || productsLoading || businessDaysLoading || hasRunRef.current) {
            return;
        }
        hasRunRef.current = true;
        initialize();
    }, [businessDaysLoading, contractId, initialize, productsLoading, shouldRepeat]);
    return {
        isInitializing: shouldRepeat && (isRunning || productsLoading || businessDaysLoading),
        initializationError,
    };
}
